'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { z } from '@/lib/validation/zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { analytics } from '@/lib/analytics/posthog'

import { FormErrorBanner } from '@/components/site/forms/form-error-banner'
import { formatMessage } from '@/components/site/forms/form-helpers'
import { Button } from '@/components/ui/button'
import { ErrorMessage, Field, Label } from '@/components/ui/fieldset'
import { Input } from '@/components/ui/input'
import { identifyByEmail, phoneForPerson } from '@/lib/analytics/identity'
import { applyApiErrors, LIMITS, requestJson } from './register-helpers'
import type { RegisterData, RegisterUi } from './register-types'

type RegisterUserResponse = Partial<RegisterData>

type UserStepProps = {
  ui: RegisterUi
  defaultValues: RegisterData
  onNext: (data: Partial<RegisterData>) => void
  onPrevious: (data: Partial<RegisterData>) => void
}

export function RegisterUserStep({ ui, defaultValues, onNext, onPrevious }: UserStepProps) {
  const firstNameId = useId()
  const lastNameId = useId()
  const phoneId = useId()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const schema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .trim()
          .min(1, ui.validation.firstNameRequired)
          .min(LIMITS.nameMin, formatMessage(ui.validation.firstNameMin, { min: LIMITS.nameMin })),
        lastName: z
          .string()
          .trim()
          .min(1, ui.validation.lastNameRequired)
          .min(LIMITS.nameMin, formatMessage(ui.validation.lastNameMin, { min: LIMITS.nameMin })),
        phone: z
          .string()
          .trim()
          .max(LIMITS.phoneMax, formatMessage(ui.validation.phoneMax, { max: LIMITS.phoneMax }))
          .optional()
          .or(z.literal('')),
      }),
    [
      ui.validation.firstNameMin,
      ui.validation.firstNameRequired,
      ui.validation.lastNameMin,
      ui.validation.lastNameRequired,
      ui.validation.phoneMax,
    ],
  )

  const {
    register,
    handleSubmit,
    setFocus,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ firstName: string; lastName: string; phone?: string }>({
    defaultValues: {
      firstName: defaultValues.firstName || '',
      lastName: defaultValues.lastName || '',
      phone: defaultValues.phone || '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    setFocus('firstName')
  }, [setFocus])

  const onSubmit = handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      const data = await requestJson<RegisterUserResponse>('register/user/check', {
        method: 'POST',
        body: JSON.stringify({ ...defaultValues, ...values }),
      })

      // Keep the person profile up to date; name is PII so it belongs on the
      // person via identify, not in capture() event properties.
      if (defaultValues.email) {
        identifyByEmail(defaultValues.email, {
          first_name: values.firstName,
          last_name: values.lastName,
          phone: phoneForPerson(values.phone),
        })
      }
      analytics.capture('signup_details_submitted', { has_phone: Boolean(values.phone) })

      onNext({ ...values, ...(data ?? {}) })
    } catch (error) {
      applyApiErrors(error, setError, setErrorMessage, ui.validation.submitFailed)
    }
  })

  const onPreviousClick = () => {
    onPrevious(defaultValues)
  }

  const labelClass = 'text-mist-900 dark:text-mist-100'
  const buttonColor = 'dark/zinc'

  return (
    <form className="space-y-5" noValidate onSubmit={onSubmit}>
      {errorMessage && <FormErrorBanner className="font-medium">{errorMessage}</FormErrorBanner>}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field>
          <Label htmlFor={firstNameId} className={labelClass} required>
            {ui.fields.firstName}
          </Label>
          <Input
            id={firstNameId}
            type="text"
            autoComplete="given-name"
            placeholder={ui.placeholders.signup.user.firstName}
            {...register('firstName')}
            invalid={Boolean(errors.firstName)}
            aria-invalid={Boolean(errors.firstName)}
            aria-describedby={errors.firstName ? `${firstNameId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.firstName?.message && (
            <ErrorMessage id={`${firstNameId}-error`}>{errors.firstName.message}</ErrorMessage>
          )}
        </Field>

        <Field>
          <Label htmlFor={lastNameId} className={labelClass} required>
            {ui.fields.lastName}
          </Label>
          <Input
            id={lastNameId}
            type="text"
            autoComplete="family-name"
            placeholder={ui.placeholders.signup.user.lastName}
            {...register('lastName')}
            invalid={Boolean(errors.lastName)}
            aria-invalid={Boolean(errors.lastName)}
            aria-describedby={errors.lastName ? `${lastNameId}-error` : undefined}
            disabled={isSubmitting}
          />
          {errors.lastName?.message && (
            <ErrorMessage id={`${lastNameId}-error`}>{errors.lastName.message}</ErrorMessage>
          )}
        </Field>
      </div>

      <Field>
        <Label htmlFor={phoneId} className={labelClass}>
          {ui.fields.phone}
        </Label>
        <Input
          id={phoneId}
          type="tel"
          autoComplete="tel"
          placeholder={ui.placeholders.signup.user.phone}
          {...register('phone')}
          invalid={Boolean(errors.phone)}
          aria-invalid={Boolean(errors.phone)}
          aria-describedby={errors.phone ? `${phoneId}-error` : undefined}
          disabled={isSubmitting}
        />
        {errors.phone?.message && <ErrorMessage id={`${phoneId}-error`}>{errors.phone.message}</ErrorMessage>}
      </Field>

      <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-between">
        <Button type="submit" size="lg" color={buttonColor} disabled={isSubmitting}>
          {ui.actions.next}
        </Button>
        <Button
          type="button"
          size="lg"
          plain
          className="!text-mist-700 hover:!text-mist-950 dark:!text-mist-300 dark:hover:!text-white"
          onClick={onPreviousClick}
          disabled={isSubmitting}
        >
          {ui.actions.back}
        </Button>
      </div>
    </form>
  )
}
